import React, { useMemo } from "react";
import { Button } from "./ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "./ui/table";
import { Printer, Square } from "lucide-react";

import useFirebaseInventory from "../hooks/useFirebaseInventory";
import { useAllFirebaseGlassData } from "../hooks/useFirebaseGlassData";
import { getHexColor } from "../helpers/glassHelpers";
import { groupInventoryItems } from "../helpers/inventoryHelpers";
import type { EnrichedInventoryItem } from "../helpers/inventoryHelpers";

const InventoryPrintPage = () => {
  const inventory = useFirebaseInventory();
  const allGlassData = useAllFirebaseGlassData();

  const groups = useMemo(() => {
    if (!inventory) return [];
    const enriched: EnrichedInventoryItem[] = Object.values(inventory).map((item) => {
      const newItem = { ...item } as EnrichedInventoryItem;
      if (newItem.glass_reference) {
        const glassItem = (allGlassData as any)?.[item.glass_type]?.inventory?.[item.glass_reference!];
        newItem.description = newItem.description
          ? glassItem?.description + "-" + newItem.description
          : glassItem?.description;
        newItem.type = glassItem?.type;
        newItem.color = glassItem?.color;
      }
      return newItem;
    });
    return groupInventoryItems(enriched);
  }, [inventory, allGlassData]);

  return (
    <div>
      <div className="print:hidden mb-6 flex items-center gap-4 border-b pb-4">
        <h2 className="text-lg font-semibold mr-auto">Print Inventory</h2>
        <Button onClick={() => window.print()}>
          <Printer className="mr-1 inline-block h-4 w-4" />
          Print
        </Button>
      </div>

      {groups.length === 0 ? (
        <p>Loading...</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Colour</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Qty</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((group) => (
              <React.Fragment key={`${group.glassType}-${group.category}`}>
                <TableRow>
                  <TableCell colSpan={5} className="bg-muted font-semibold print:bg-gray-100">
                    {group.glassType} — {group.category}
                  </TableCell>
                </TableRow>
                {group.items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.glass_reference}</TableCell>
                    <TableCell>
                      <span
                        style={{
                          color: row.color?.multicolored ? "black" : getHexColor(row.color?.rgb),
                          fontSize: row.color?.multicolored ? 14 : 24,
                        }}
                      >
                        {row.color?.multicolored ? (
                          "Multi"
                        ) : (
                          row.color && <Square className="inline-block h-4 w-4 fill-current" />
                        )}
                      </span>
                    </TableCell>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell>{row.count}</TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default InventoryPrintPage;
